import { computed, defineComponent, Fragment, Text, h, isVNode } from 'vue';

import {
  classNames,
  eagerComputed,
  flattenChildren,
  isEmptyElement,
  isValidElement,
  parseStyleText,
  warn
} from '@components/utils';

import { useInjectHover, useInjectSlots, useInjectSticky } from '../../context';
import { customRenderSlot, getPathValue, validateValue } from '../../utils';
import { INTERNAL_COL_DEFINE } from '../../constants';

import type { VNodeArrayChildren, VNode, CSSProperties } from 'vue';
import type { NTable } from '../../interface';

interface ICellProps<RecordType = NTable.TDefaultRecord> {
  record?: RecordType;
  /** `column` index is the real show rowIndex */
  index?: number;
  /** the index of the record. For the render(value, record, renderIndex) */
  renderIndex?: number;
  dataIndex?: NTable.TDataIndex;
  customRender?: NTable.NColumn.IColumn<RecordType>['customRender'];
  component?: NTable.NCustomize.TCustomizeComponent;
  colSpan?: number;
  rowSpan?: number;
  ellipsis?: NTable.NCell.TCellEllipsis;
  align?: NTable.TAlign;

  // Fixed
  fixLeft?: number | false;
  fixRight?: number | false;
  firstFixLeft?: boolean;
  lastFixLeft?: boolean;
  firstFixRight?: boolean;
  lastFixRight?: boolean;

  // Additional
  appendNode?: any;
  additionalProps?: NTable.NCustomize.TAdditionalProps;
  rowType?: 'header' | 'body' | 'footer';
  isSticky?: boolean;
  column?: NTable.NColumn.IColumn<RecordType>;
  cellType?: 'header' | 'body';
  transformCellText?: NTable.NCell.TTransformCellText<RecordType>;
}

export default defineComponent<ICellProps>({
  name: 'Cell',
  props: [
    'record',
    'index',
    'renderIndex',
    'dataIndex',
    'customRender',
    'component',
    'colSpan',
    'rowSpan',
    'ellipsis',
    'align',
    'fixLeft',
    'fixRight',
    'firstFixLeft',
    'lastFixLeft',
    'firstFixRight',
    'lastFixRight',
    'appendNode',
    'additionalProps',
    'rowType',
    'isSticky',
    'column',
    'cellType',
    'transformCellText'
  ] as any,
  setup(props, { slots }) {
    const contextSlots = useInjectSlots();
    const { startRow, endRow, onHover } = useInjectHover();
    const supportSticky = useInjectSticky();

    const colSpan = computed(
      () => props.colSpan ?? props.additionalProps?.colSpan ?? (props.additionalProps?.colspan as number)
    );
    const rowSpan = computed(
      () => props.rowSpan ?? props.additionalProps?.rowSpan ?? (props.additionalProps?.rowspan as number)
    );
    const hovering = eagerComputed(() => {
      const { index } = props;
      return isHoverRange(index, rowSpan.value || 1, startRow.value, endRow.value);
    });

    // ====================== Hover =======================
    const onMouseenter = (event: MouseEvent, mergedRowSpan: number) => {
      const { record, index, additionalProps } = props;

      if (record) {
        onHover(index, (index || 0) + mergedRowSpan - 1);
      }

      additionalProps?.onMouseenter?.(event);
    };

    const onMouseleave = (event: MouseEvent) => {
      const { record, additionalProps } = props;

      if (record) {
        onHover(-1, -1);
      }

      additionalProps?.onMouseleave?.(event);
    };
    // ====================== Hover =======================

    const getTitle = (vnodes: VNodeArrayChildren): any => {
      const vnode = filterEmpty(vnodes)[0];

      if (isVNode(vnode)) {
        if (vnode.type === Text) {
          return vnode.children;
        } else {
          return Array.isArray(vnode.children) ? getTitle(vnode.children) : undefined;
        }
      } else {
        return vnode;
      }
    };

    return () => {
      const {
        record,
        index,
        renderIndex,
        dataIndex,
        customRender,
        component: Component = 'td',
        fixLeft,
        fixRight,
        firstFixLeft,
        lastFixLeft,
        firstFixRight,
        lastFixRight,
        appendNode = slots.appendNode?.(),
        additionalProps = {},
        ellipsis,
        align,
        rowType,
        isSticky,
        column = {},
        cellType
      } = props;

      // ==================== Child Node ====================
      let cellProps: NTable.NCell.ICell | undefined;
      let childNode: VNodeArrayChildren | undefined | unknown;

      const children = slots.default?.();

      if (validateValue(children) || cellType === 'header') {
        childNode = children;
      } else {
        const value = getPathValue(record, dataIndex);

        // Customize render node
        childNode = value;

        if (customRender) {
          const renderData = customRender({
            text: value,
            value,
            record,
            index,
            renderIndex,
            column: column.__originColumn__
          });

          if (isRenderCell(renderData)) {
            warn(
              'CdsTable',
              '`columns.customRender` return cell props is deprecated with perf issue, please use `customCell` instead.'
            );

            childNode = renderData.children;
            cellProps = renderData.props;
          } else {
            childNode = renderData;
          }
        }

        if (!(INTERNAL_COL_DEFINE in column) && cellType === 'body' && contextSlots.value.bodyCell) {
          const child = customRenderSlot(
            contextSlots.value,
            'bodyCell',
            {
              text: value,
              value,
              record,
              index,
              column: column.__originColumn__
            },
            () => {
              const fallback = childNode === undefined ? value : childNode;
              return [
                (typeof fallback === 'object' && isValidElement(fallback)) || typeof fallback !== 'object'
                  ? fallback
                  : null
              ] as VNodeArrayChildren;
            }
          );

          childNode = flattenChildren(child as any);
        }

        if (props.transformCellText) {
          childNode = props.transformCellText({
            text: childNode,
            record,
            index,
            column: column.__originColumn__
          });
        }
      }

      // Not crash if final `childNode` is not validate VueNode
      if (typeof childNode === 'object' && !Array.isArray(childNode) && !isVNode(childNode)) {
        childNode = null;
      }

      if (ellipsis && (lastFixLeft || firstFixRight)) {
        childNode = h('span', { class: 'cds-table__cell-content' }, childNode as VNodeArrayChildren);
      }

      if (Array.isArray(childNode) && childNode?.length === 1) {
        childNode = childNode[0];
      }

      const {
        colSpan: cellColSpan,
        rowSpan: cellRowSpan,
        style: cellStyle,
        class: cellClassName,
        ...restCellProps
      } = cellProps || {};
      const mergedColSpan = (cellColSpan !== undefined ? cellColSpan : colSpan.value) ?? 1;
      const mergedRowSpan = (cellRowSpan !== undefined ? cellRowSpan : rowSpan.value) ?? 1;

      if (mergedColSpan === 0 || mergedRowSpan === 0) {
        return null;
      }
      // ==================== Child Node ====================

      // ====================== Fixed =======================
      const fixedStyle: CSSProperties = {};
      const isFixLeft = typeof fixLeft === 'number' && supportSticky.value;
      const isFixRight = typeof fixRight === 'number' && supportSticky.value;

      if (isFixLeft) {
        fixedStyle.position = 'sticky';
        fixedStyle.left = `${fixLeft}px`;
      }

      if (isFixRight) {
        fixedStyle.position = 'sticky';
        fixedStyle.right = `${fixRight}px`;
      }
      // ====================== Fixed =======================

      // ====================== Align =======================
      const alignStyle: CSSProperties = {};

      if (align) {
        alignStyle.textAlign = align;
      }
      // ====================== Align =======================

      // ====================== Render ======================
      let title: string = '';

      const ellipsisConfig: NTable.NCell.TCellEllipsis = ellipsis === true ? { showTitle: true } : ellipsis;

      if (ellipsisConfig && (ellipsisConfig.showTitle || rowType === 'header')) {
        if (typeof childNode === 'string' || typeof childNode === 'number') {
          title = childNode.toString();
        } else if (isVNode(childNode)) {
          title = getTitle([childNode]);
        }
      }

      const componentProps = {
        title,
        ...restCellProps,
        ...additionalProps,
        colSpan: mergedColSpan !== 1 ? mergedColSpan : null,
        rowSpan: mergedRowSpan !== 1 ? mergedRowSpan : null,
        class: classNames('cds-table__cell', cellClassName, additionalProps.class, {
          ['cds-table__cell--fix-left']: isFixLeft && supportSticky.value,
          ['cds-table__cell--fix-left-first']: firstFixLeft && supportSticky.value,
          ['cds-table__cell--fix-left-last']: lastFixLeft && supportSticky.value,
          ['cds-table__cell--fix-right']: isFixRight && supportSticky.value,
          ['cds-table__cell--fix-right-first']: firstFixRight && supportSticky.value,
          ['cds-table__cell--fix-right-last']: lastFixRight && supportSticky.value,
          ['cds-table__cell--ellipsis']: ellipsis,
          ['cds-table__cell--with-append']: appendNode,
          ['cds-table__cell--fix-sticky']: (isFixLeft || isFixRight) && isSticky && supportSticky.value,
          ['cds-table__cell--row-hover']: !cellProps && hovering.value
        }),
        onMouseenter: (event: MouseEvent) => {
          onMouseenter(event, mergedRowSpan);
        },
        onMouseleave,
        style: {
          ...parseStyleText(additionalProps.style as any),
          ...alignStyle,
          ...fixedStyle,
          ...cellStyle
        }
      };

      return h(Component, { ...componentProps }, [appendNode, childNode, slots.dragHandle?.()]);
      // ====================== Render ======================
    };
  }
});

/**
 * Check if cell is in hover range
 * @param cellStartRow
 * @param cellRowSpan
 * @param startRow
 * @param endRow
 */
function isHoverRange(cellStartRow: number = 0, cellRowSpan: number, startRow: number, endRow: number) {
  const cellEndRow = cellStartRow + cellRowSpan - 1;
  return cellStartRow <= endRow && cellEndRow >= startRow;
}

function filterEmpty(children: VNodeArrayChildren = []): VNodeArrayChildren {
  const res: VNodeArrayChildren = [];
  children.forEach((child: any) => {
    if (Array.isArray(child)) {
      res.push(...child);
    } else if (child?.type === Fragment) {
      res.push(...filterEmpty(child.children));
    } else {
      res.push(child);
    }
  });
  return res.filter((c) => !isEmptyElement(c as VNode));
}

function isRenderCell<RecordType = NTable.TDefaultRecord>(
  data: NTable.NCell.IRenderedCell<RecordType>
): data is NTable.NCell.IRenderedCell<RecordType> {
  return data && typeof data === 'object' && !Array.isArray(data) && !isVNode(data);
}
